import type { Paths } from '@src/utils/types';
import type { ReactNode } from 'react';
import type { EntityAdminRecord } from '../../types';
import { hideDrawerOption } from '../shared/drawerUtils';
import { isOptionDivider, type NonDividerDrawerOption } from './components/dividerUtils';
import { getOptionLabel } from './getOptionLabel';
import type { DividerDrawerOption, InputsAndDropdownsDrawerOption, InputsAndDropdownsDrawerOptions } from './types';

export type MappedDividerOption<TDrawerRecord extends EntityAdminRecord> = {
  key: string;
  option: DividerDrawerOption<TDrawerRecord>;
  label: undefined;
  inheritedValue: undefined;
};

export type MappedNonDividerOption<TDrawerRecord extends EntityAdminRecord> = {
  key: string;
  option: NonDividerDrawerOption<TDrawerRecord>;
  label: ReactNode;
  inheritedValue: string | undefined;
};

export type MappedDrawerOption<TDrawerRecord extends EntityAdminRecord> =
  | MappedDividerOption<TDrawerRecord>
  | MappedNonDividerOption<TDrawerRecord>;

export type MappedDrawerOptions<TDrawerRecord extends EntityAdminRecord> = Array<
  MappedDrawerOption<TDrawerRecord> | MappedDrawerOption<TDrawerRecord>[]
>;

export function flattenDrawerOptions<TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>
): InputsAndDropdownsDrawerOption<TDrawerRecord>[] {
  return drawerOptions.flatMap(option => (Array.isArray(option) ? option : [option]));
}

export function filterAndMapDrawerOptions<TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>,
  form: TDrawerRecord,
  originalEntity: TDrawerRecord | undefined,
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>
): MappedDrawerOption<TDrawerRecord>[] {
  return flattenDrawerOptions(drawerOptions)
    .filter(option => !hideDrawerOption(option, form, originalEntity))
    .map(
      (option, index): MappedDrawerOption<TDrawerRecord> =>
        mapDrawerOptionWithForm(option, index, form, inheritedValues)
    );
}

export function filterAndMapGroupedDrawerOptions<TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>,
  form: TDrawerRecord,
  originalEntity: TDrawerRecord | undefined,
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>
): MappedDrawerOptions<TDrawerRecord> {
  return drawerOptions.reduce<MappedDrawerOptions<TDrawerRecord>>((acc, optionOrGroup, index) => {
    if (!Array.isArray(optionOrGroup)) {
      if (!hideDrawerOption(optionOrGroup, form, originalEntity)) {
        acc.push(mapDrawerOptionWithForm(optionOrGroup, index, form, inheritedValues));
      }
      return acc;
    }

    const group = optionOrGroup
      .filter(option => !hideDrawerOption(option, form, originalEntity))
      .map((option, groupIndex) => mapDrawerOptionWithForm(option, groupIndex, form, inheritedValues));

    if (group.length > 0) {
      acc.push(group);
    }
    return acc;
  }, []);
}

function mapDrawerOptionWithForm<TDrawerRecord extends EntityAdminRecord>(
  option: InputsAndDropdownsDrawerOption<TDrawerRecord>,
  index: number,
  form: TDrawerRecord,
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>
): MappedDrawerOption<TDrawerRecord> {
  if (isOptionDivider(option)) {
    return { key: `divider-${index}`, option, label: undefined, inheritedValue: undefined };
  }
  return {
    key: option.field,
    option,
    label: getOptionLabel(option, form),
    inheritedValue: inheritedValues[option.field],
  };
}
